import React, { useState, useEffect } from 'react';
import { FiRefreshCw, FiCalendar, FiZap, FiCheckCircle, FiAlertTriangle,FiEdit2 ,FiLayers, FiSettings } from 'react-icons/fi';
import { FaShopify } from 'react-icons/fa';
import { FiTag, FiImage, FiClock, FiSmartphone, FiCode } from 'react-icons/fi';


export default function IssueDetectionPage (){
    const technicalSEOFeatures = [
      {
        icon: <FiAlertTriangle className="text-2xl text-red-500" />,
        title: "Broken Links (404 Errors)",
        description: "Automatically detect and list all broken internal/external links with suggested fixes.",
        benefit: "Improves user experience and prevents ranking penalties."
      },
      {
        icon: <FiTag className="text-2xl text-blue-600" />,
        title: "Meta Tag Issues",
        description: "Identify missing, duplicate, or poorly optimized meta titles and descriptions.",
        benefit: "Ensures proper search engine visibility and click-through rates."
      },
      {
        icon: <FiImage className="text-2xl text-teal-500" />,
        title: "Missing Alt Text",
        description: "Detect images without alt attributes and provide AI-generated suggestions.",
        benefit: "Enhances accessibility and image search rankings."
      },
      {
        icon: <FiClock className="text-2xl text-amber-500" />,
        title: "Slow-Loading Pages",
        description: "Analyze page speed metrics with actionable recommendations.",
        benefit: "Reduces bounce rates and improves Core Web Vitals scores."
      },
      {
        icon: <FiSmartphone className="text-2xl text-purple-500" />,
        title: "Mobile Responsiveness",
        description: "Check for mobile usability issues like viewport configuration or touch elements.",
        benefit: "Critical for mobile-first indexing and user retention."
      },
      {
        icon: <FiCode className="text-2xl text-indigo-500" />,
        title: "Structured Data Errors",
        description: "Validate Schema markup for products, breadcrumbs, and reviews.",
        benefit: "Prevents rich snippet losses in search results."
      }
    ];
  
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-gradient-to-r from-blue-800 to-teal-500 text-white py-6 px-6 shadow-md">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FaShopify className="text-3xl" />
              <span className="text-2xl font-bold">SEOAutoFix</span>
            </div>
            <nav className="hidden md:flex space-x-8">
              <a href="#" className="hover:text-blue-100 transition">Dashboard</a>
              <a href="#" className="hover:text-blue-100 transition">Reports</a>
              <a href="#" className="hover:text-blue-100 transition">Settings</a>
            </nav>
            <button className="bg-white text-blue-800 px-4 py-2 rounded-md font-medium hover:bg-blue-50 transition">
              Run New Scan
            </button>
          </div>
        </header>
  
        {/* Main Content */}
        <main className="max-w-7xl mx-auto py-12 px-6">
          {/* Hero Section */}
          <section className="mb-16 text-center">
            <h1 className="text-4xl font-bold text-gray-800 mb-4">
              <span className="bg-gradient-to-r from-blue-800 to-teal-500 bg-clip-text text-transparent">
                Technical SEO
              </span> Issue Detection
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Automatically identify and prioritize critical SEO problems hurting your Shopify store's rankings.
            </p>
          </section>
  
          {/* Priority Badges */}
          <div className="flex justify-center mb-12 space-x-6">
            <div className="flex items-center">
              <div className="w-4 h-4 rounded-full bg-red-500 mr-2"></div>
              <span className="text-gray-700 font-medium">Critical</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 rounded-full bg-yellow-500 mr-2"></div>
              <span className="text-gray-700 font-medium">Warning</span>
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 rounded-full bg-green-500 mr-2"></div>
              <span className="text-gray-700 font-medium">Optimized</span>
            </div>
          </div>
  
          {/* Features Grid */}
          <section className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {technicalSEOFeatures.map((feature, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition border border-gray-100"
              >
                <div className="flex items-center mb-4">
                  <div className="mr-4 p-3 rounded-lg bg-blue-50">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">{feature.title}</h3>
                </div>
                <p className="text-gray-600 mb-4">{feature.description}</p>
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm text-blue-800 font-medium">
                    <span className="font-bold">Benefit:</span> {feature.benefit}
                  </p>
                </div>
              </div>
            ))}
          </section>
  
          {/* How It Works */}
          <section className="bg-gradient-to-br from-blue-800 to-teal-500 rounded-2xl p-8 text-white mb-16">
            <h2 className="text-2xl font-bold mb-6">How Our Detection System Works</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white bg-opacity-10 p-6 rounded-xl">
                <div className="text-3xl font-bold mb-2">1</div>
                <h3 className="text-xl font-semibold mb-2">Deep Crawl</h3>
                <p>We analyze every page of your store like search engines do.</p>
              </div>
              <div className="bg-white bg-opacity-10 p-6 rounded-xl">
                <div className="text-3xl font-bold mb-2">2</div>
                <h3 className="text-xl font-semibold mb-2">AI Analysis</h3>
                <p>Machine learning identifies patterns humans might miss.</p>
              </div>
              <div className="bg-white bg-opacity-10 p-6 rounded-xl">
                <div className="text-3xl font-bold mb-2">3</div>
                <h3 className="text-xl font-semibold mb-2">Smart Prioritization</h3>
                <p>Issues are ranked by potential impact on rankings.</p>
              </div>
            </div>
          </section>
  
          {/* CTA Section */}
          <section className="text-center">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Ready to Fix Your SEO Issues?</h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Our automated fixes can resolve 80% of technical SEO problems with one click.
            </p>
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <button className="bg-gradient-to-r from-blue-800 to-teal-500 text-white px-8 py-4 rounded-lg font-medium hover:opacity-90 transition shadow-lg">
                Fix Issues Now
              </button>
              <button className="border-2 border-blue-800 text-blue-800 px-8 py-4 rounded-lg font-medium hover:bg-blue-50 transition">
                View Sample Report
              </button>
            </div>
          </section>
        </main>
  
        {/* Footer */}
        <footer className="bg-gray-800 text-gray-400 py-12 px-6">
          <div className="max-w-7xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <FaShopify className="text-2xl text-teal-400 mr-2" />
              <span className="text-xl font-bold text-white">SEOAutoFix</span>
            </div>
            <p className="mb-6">Automated SEO optimization for Shopify stores</p>
            <div className="flex justify-center space-x-6 mb-6">
              <a href="#" className="hover:text-white transition">Privacy</a>
              <a href="#" className="hover:text-white transition">Terms</a>
              <a href="#" className="hover:text-white transition">Contact</a>
            </div>
            <p>© {new Date().getFullYear()} SEOAutoFix. All rights reserved.</p>
          </div>
        </footer>
      </div>
    );
  };
  